import {
    DataTable,
    EditButton,
    ReferenceField,
    ReferenceManyField,
    TextField,
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    AlertTriangle,
    ArrowLeft,
    Building2,
    ClipboardList,
    FileText,
    Globe,
    History,
    Mail,
    Phone,
    ShieldCheck,
    Star,
    Tag,
    User,
} from 'lucide-react';
import { ShowBase, useShowContext } from 'ra-core';
import { Link } from 'react-router-dom';
import { StatusBadge } from '../documents/shared/StatusBadge';
import { VENDOR_DOCUMENT_CHECKLIST } from './vendorDocumentChecklist';
import { ApproveVendorButton } from './ApproveVendorButton';
import { BlacklistVendorButton } from './BlacklistVendorButton';
import { CheckVendorButton } from './CheckVendorButton';
import { HoldVendorButton } from './HoldVendorButton';
import { VendorInviteButton } from './VendorInviteButton';

const VendorShowContent = () => {
    const { record, isPending } = useShowContext();
    if (isPending || !record) return null;

    const additionalContacts = Array.isArray(record.contact_persons)
        ? record.contact_persons.filter((contact: any) =>
            contact?.name || contact?.designation || contact?.email || contact?.phone
        )
        : [];

    return (
        <div className="space-y-6">
            <Card className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
                <CardContent className="p-4 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-4 flex-wrap">
                        <Button asChild variant="outline" size="sm" className="gap-1.5 bg-primary/10 hover:bg-primary/20 text-primary border-primary/20 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                            <Link to="/vendors" className="flex items-center gap-1.5">
                                <ArrowLeft className="w-4 h-4" /> Back to Vendors
                            </Link>
                        </Button>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="text-2xl font-bold tracking-tight text-slate-900">{record.name}</h1>
                            <StatusBadge status={record.status || 'active'} />
                            {record.code && (
                                <span className="font-mono font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded text-xs border">
                                    {record.code}
                                </span>
                            )}
                            <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 font-semibold">
                                {record.category || 'General Supplier'}
                            </Badge>
                            {record.material_service && (
                                <span className="text-slate-500 text-xs font-medium">{record.material_service}</span>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        <CheckVendorButton />
                        <ApproveVendorButton />
                        <HoldVendorButton />
                        <BlacklistVendorButton />
                        <VendorInviteButton />
                        <EditButton />
                    </div>
                </CardContent>
            </Card>

            <Tabs defaultValue="details" className="w-full">
                <TabsList className="mb-4">
                    <TabsTrigger value="details">Company Details</TabsTrigger>
                    <TabsTrigger value="evaluation">Evaluation & Performance</TabsTrigger>
                    <TabsTrigger value="documents">Documents</TabsTrigger>
                    <TabsTrigger value="history">History & POs</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="mt-4 space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <Card className="border border-slate-200 shadow-sm rounded-xl bg-white">
                            <CardHeader className="pb-3 border-b bg-slate-50/30">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                    <Building2 className="w-3.5 h-3.5 text-primary" /> Physical Address & Contacts
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-4 space-y-4">
                                <div className="flex gap-3">
                                    <Building2 className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Office Address</span>
                                        <div className="text-sm font-semibold text-slate-900 leading-relaxed">
                                            {record.address || 'No address provided.'}
                                            {(record.city || record.state || record.country) && (
                                                <div className="text-slate-500 font-medium text-xs mt-0.5">
                                                    {[record.city, record.state, record.country].filter(Boolean).join(', ')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t border-slate-100 pt-4 flex gap-3">
                                    <Phone className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Company Phone</span>
                                        <div className="text-sm font-semibold text-slate-700">{record.phone || 'N/A'}</div>
                                    </div>
                                </div>

                                <div className="border-t border-slate-100 pt-4 flex gap-3">
                                    <Mail className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Company Email</span>
                                        <div className="text-sm font-semibold text-slate-700">{record.email || 'N/A'}</div>
                                    </div>
                                </div>

                                <div className="border-t border-slate-100 pt-4 flex gap-3">
                                    <Globe className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Website</span>
                                        <div className="text-sm font-semibold text-slate-700">
                                            {record.website ? (
                                                <a href={record.website.startsWith('http') ? record.website : `https://${record.website}`} target="_blank" rel="noopener noreferrer" className="hover:underline text-primary">
                                                    {record.website}
                                                </a>
                                            ) : 'No website'}
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border border-slate-200 shadow-sm rounded-xl bg-white">
                            <CardHeader className="pb-3 border-b bg-slate-50/30">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                    <User className="w-3.5 h-3.5 text-primary" /> Primary Contact Person
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-4 space-y-4">
                                <div className="flex items-center gap-3 p-3 bg-slate-50/60 rounded-xl border border-slate-100">
                                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center font-bold text-primary shrink-0">
                                        C
                                    </div>
                                    <div>
                                        <div className="font-semibold text-sm text-slate-900">
                                            {record.contact_person || 'No contact person'}
                                        </div>
                                        <div className="text-xs text-slate-500">
                                            {record.designation || 'Contact Representative'}
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t border-slate-100 pt-4 flex gap-3">
                                    <Phone className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Direct Phone</span>
                                        <div className="text-sm font-semibold text-slate-700">{record.phone || 'N/A'}</div>
                                    </div>
                                </div>

                                <div className="border-t border-slate-100 pt-4 flex gap-3">
                                    <Mail className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Direct Email</span>
                                        <div className="text-sm font-semibold text-slate-700">{record.email || 'N/A'}</div>
                                    </div>
                                </div>

                                {additionalContacts.length > 0 && (
                                    <div className="border-t border-slate-100 pt-4 space-y-3">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Additional Contacts</span>
                                        {additionalContacts.map((contact: any, index: number) => (
                                            <div key={index} className="rounded-lg border border-slate-100 bg-slate-50/60 p-3 text-xs">
                                                <div className="font-semibold text-slate-900">
                                                    {contact.name || `Contact ${index + 1}`}
                                                </div>
                                                {contact.designation && (
                                                    <div className="text-slate-500">{contact.designation}</div>
                                                )}
                                                {(contact.email || contact.phone) && (
                                                    <div className="mt-1 text-slate-600">
                                                        {[contact.email, contact.phone].filter(Boolean).join(' | ')}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card className="shadow-sm border border-slate-200 rounded-xl bg-white">
                            <CardHeader className="pb-3 border-b bg-slate-50/30">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-700 flex items-center gap-2">
                                    Quick Facts
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-4 space-y-4 text-xs">
                                <div>
                                    <span className="text-[10px] text-slate-400 uppercase font-bold block mb-1">Risk Profile</span>
                                    <Badge variant="outline" className={
                                        record.risk_level === 'High' ? 'bg-red-50 text-red-700 border-red-200 font-semibold' :
                                        record.risk_level === 'Medium' ? 'bg-amber-50 text-amber-700 border-amber-200 font-semibold' :
                                        'bg-green-50 text-green-700 border-green-200 font-semibold'
                                    }>
                                        {record.risk_level || 'Low Risk'}
                                    </Badge>
                                </div>
                                <div>
                                    <span className="text-[10px] text-slate-400 uppercase font-bold block mb-1">Evaluation Score</span>
                                    <span className="text-sm font-bold text-slate-800">{record.evaluation_score != null ? `${record.evaluation_score}%` : 'Not evaluated'}</span>
                                </div>
                                <div>
                                    <span className="text-[10px] text-slate-400 uppercase font-bold block mb-1">Payment Terms</span>
                                    <span className="text-sm font-semibold text-slate-800">{record.payment_terms || 'N/A'}</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <Card className="border border-slate-200 shadow-sm rounded-xl bg-white">
                        <CardHeader className="pb-3 border-b bg-slate-50/30">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-700 flex items-center gap-2">
                                <ShieldCheck className="w-3.5 h-3.5 text-primary" /> Review & Approval
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                            <div>
                                <span className="text-slate-400 uppercase font-bold text-[10px] block mb-1">Checked By</span>
                                <span className="font-semibold text-slate-700">{record.checked_by || '-'}</span>
                            </div>
                            <div>
                                <span className="text-slate-400 uppercase font-bold text-[10px] block mb-1">Approved By</span>
                                <span className="font-semibold text-slate-700">{record.approved_by || '-'}</span>
                            </div>
                            {record.status === 'blacklisted' && (
                                <div>
                                    <span className="text-red-400 uppercase font-bold text-[10px] block mb-1">Blacklist Reason</span>
                                    <span className="text-red-600 italic">{record.blacklist_reason || 'No reason recorded'}</span>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="border border-slate-200 shadow-sm rounded-xl bg-white overflow-hidden w-full">
                        <CardHeader className="bg-slate-50/30 border-b">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest flex items-center gap-2 text-slate-500">
                                    <ShieldCheck className="w-4 h-4 text-green-600" /> Compliance & Banking Details
                                </CardTitle>
                                {record.is_approved && (
                                    <Badge className="bg-green-100 text-green-700 border-green-200">KYC Verified</Badge>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                            <div className="p-6 space-y-4">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-700 block border-b pb-2">Compliance Information</span>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">GSTIN / Tax ID</span>
                                        <div className="font-mono font-bold text-sm text-slate-900 bg-slate-50 px-3 py-2 rounded-lg border border-slate-100/50 w-fit">{record.gst_number || 'NOT PROVIDED'}</div>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">PAN Number</span>
                                        <div className="font-mono font-bold text-sm text-slate-900 bg-slate-50 px-3 py-2 rounded-lg border border-slate-100/50 w-fit">{record.pan_number || 'NOT PROVIDED'}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 space-y-4">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-700 block border-b pb-2">Banking & Terms</span>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Bank Name</span>
                                        <div className="text-sm font-semibold text-slate-800">{record.bank_name || 'N/A'}</div>
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Account Number</span>
                                        <div className="text-sm font-mono font-bold text-slate-800">{record.bank_account_number || 'N/A'}</div>
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">IFSC Code</span>
                                        <div className="text-sm font-mono font-bold text-slate-800">{record.bank_ifsc_code || 'N/A'}</div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="evaluation" className="mt-4 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border border-slate-200 shadow-sm rounded-xl bg-white">
                            <CardHeader className="pb-3 border-b bg-slate-50/30">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                    <Tag className="w-3.5 h-3.5 text-primary" /> Specialization Tags
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-4 flex flex-wrap gap-2">
                                {record.specialization_tags?.map((tag: string) => (
                                    <Badge key={tag} variant="outline" className="bg-primary/5 text-primary border-primary/10 hover:bg-primary/10 transition-colors px-2.5 py-1 text-xs font-semibold">
                                        {tag}
                                    </Badge>
                                )) || <span className="text-xs text-slate-400 italic">No specialization tags defined.</span>}
                            </CardContent>
                        </Card>

                        <Card className="border border-slate-200 shadow-sm rounded-xl bg-white">
                            <CardHeader className="pb-3 border-b bg-slate-50/30">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                    <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" /> Past Performance
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-4 space-y-3">
                                <div className="flex items-center gap-2">
                                    <span className="text-2xl font-black text-slate-900">{record.performance_rating || 'N/A'}</span>
                                    <div className="flex gap-0.5">
                                        {[1, 2, 3, 4, 5].map((s) => (
                                            <Star key={s} className={`w-4 h-4 ${s <= (record.performance_rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                                        ))}
                                    </div>
                                </div>
                                {record.past_performance_notes && (
                                    <p className="text-xs text-slate-600 leading-relaxed italic border-l-2 border-slate-200 pl-3 bg-slate-50 p-2.5 rounded-r-lg">
                                        {record.past_performance_notes}
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {(record.evaluation_score != null || record.risk_level || record.last_evaluation_date || record.next_due_date || record.evaluation_remarks) && (
                        <Card className="border border-slate-200 shadow-sm rounded-xl bg-white">
                            <CardHeader className="pb-3 border-b bg-slate-50/30">
                                <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                    <ClipboardList className="w-3.5 h-3.5 text-primary" /> Vendor Evaluation Profile
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Evaluation Score</span>
                                        <div className="text-lg font-black text-primary">
                                            {record.evaluation_score != null ? `${record.evaluation_score}%` : '-'}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Risk Level</span>
                                        <div>
                                            {record.risk_level ? (
                                                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                                                    record.risk_level === 'High' ? 'bg-red-100 text-red-700' :
                                                    record.risk_level === 'Medium' ? 'bg-amber-100 text-amber-700' :
                                                    'bg-green-100 text-green-700'
                                                }`}>{record.risk_level}</span>
                                            ) : '-'}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Last Evaluation</span>
                                        <div className="text-sm font-semibold text-slate-700">
                                            {record.last_evaluation_date ?? '-'}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Next Evaluation Due</span>
                                        <div className="text-sm font-semibold text-slate-700">
                                            {record.next_due_date ?? '-'}
                                        </div>
                                    </div>
                                </div>
                                {record.evaluation_remarks && (
                                    <p className="mt-4 text-xs text-slate-600 italic border-l-2 border-primary/20 pl-3 bg-slate-50 p-2.5 rounded-r-lg">
                                        {record.evaluation_remarks}
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                <TabsContent value="documents" className="mt-4 space-y-6">
                    {(() => {
                        const saved = Array.isArray(record.documents) ? record.documents : [];
                        const byType = new Map(saved.map((d: any) => [d.doc_type, d]));
                        const missingMandatory = VENDOR_DOCUMENT_CHECKLIST.filter(
                            (item) => item.mandatory && !(byType.get(item.doc_type) as any)?.file_url
                        );
                        return (
                            <Card className="border border-slate-200 shadow-sm rounded-xl bg-white">
                                <CardHeader className="pb-3 border-b bg-slate-50/50 flex flex-row items-center justify-between">
                                    <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                        <FileText className="w-3.5 h-3.5 text-slate-400" /> Document Checklist
                                    </CardTitle>
                                    {missingMandatory.length > 0 ? (
                                        <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 rounded px-2 py-0.5 flex items-center gap-1">
                                            <AlertTriangle className="w-3 h-3" /> {missingMandatory.length} required missing
                                        </span>
                                    ) : (
                                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                                            All required uploaded
                                        </span>
                                    )}
                                </CardHeader>
                                <CardContent className="pt-4 divide-y divide-slate-100">
                                    {VENDOR_DOCUMENT_CHECKLIST.map((item, i) => {
                                        const doc = byType.get(item.doc_type) as any;
                                        return (
                                            <div key={item.doc_type} className="flex items-center justify-between gap-3 py-2.5">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="text-xs font-medium text-slate-700 truncate">
                                                        {i + 1}. {item.doc_name}
                                                    </span>
                                                    {item.mandatory && (
                                                        <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 shrink-0">
                                                            Required
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 shrink-0">
                                                    {doc?.remarks && (
                                                        <span className="text-[11px] text-slate-400 italic max-w-[180px] truncate">{doc.remarks}</span>
                                                    )}
                                                    {doc?.file_url ? (
                                                        <a
                                                            href={doc.file_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-xs font-semibold text-[#00a68d] hover:underline flex items-center gap-1"
                                                        >
                                                            <FileText className="w-3.5 h-3.5" /> View
                                                        </a>
                                                    ) : (
                                                        <span className="text-[11px] text-slate-400">Not uploaded</span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </CardContent>
                            </Card>
                        );
                    })()}
                </TabsContent>

                <TabsContent value="history" className="mt-4 space-y-6">
                    <Card className="shadow-sm border border-slate-200 rounded-xl bg-white">
                        <CardHeader className="pb-3 border-b bg-slate-50/30">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-700 flex items-center gap-2">
                                <History className="w-3.5 h-3.5 text-slate-400" /> Linked Purchase Orders
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            <ReferenceManyField reference="purchase_orders" target="vendor_id">
                                <DataTable rowClassName={() => "hover:bg-primary/5 transition-colors odd:bg-muted/40"}>
                                    <DataTable.Col source="reference" label="PO No." cellClassName="py-3 px-4 font-semibold text-slate-900" />
                                    <DataTable.Col source="status" label="Status" cellClassName="py-3 px-4" />
                                    <DataTable.NumberCol source="total_amount" label="Amount" options={{ style: 'currency', currency: 'INR' }} cellClassName="py-3 px-4" />
                                    <DataTable.Col source="date" label="Date" cellClassName="py-3 px-4" />
                                </DataTable>
                            </ReferenceManyField>
                        </CardContent>
                    </Card>

                    <Card className="shadow-sm border border-slate-200 rounded-xl bg-white">
                        <CardHeader className="pb-3 border-b bg-slate-50/30">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-slate-700 flex items-center gap-2">
                                <History className="w-3.5 h-3.5 text-slate-400" /> Project History
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            <ReferenceManyField reference="project_vendors" target="vendor_id">
                                <DataTable rowClassName={() => "hover:bg-primary/5 transition-colors odd:bg-muted/40"}>
                                    <DataTable.Col label="Project" source="project_id" cellClassName="py-3 px-4">
                                        <ReferenceField source="project_id" reference="projects" link="show">
                                            <span className="text-xs font-bold">
                                                <TextField source="name" />
                                            </span>
                                        </ReferenceField>
                                    </DataTable.Col>
                                    <DataTable.Col source="role" label="Role" cellClassName="py-3 px-4 text-xs text-slate-600 font-medium" />
                                </DataTable>
                            </ReferenceManyField>
                        </CardContent>
                    </Card>

                    <Card className="border border-slate-200 shadow-sm rounded-xl bg-white">
                        <CardHeader className="pb-3 border-b bg-red-50/30">
                            <CardTitle className="text-xs font-bold uppercase tracking-widest text-red-600 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-red-500" /> Manager Review Flags
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <ReferenceManyField reference="supplier_flags" target="vendor_id">
                                <DataTable rowClassName={() => "hover:bg-primary/5 transition-colors odd:bg-muted/40"}>
                                    <DataTable.Col source="created_at" label="Date" cellClassName="py-3 px-4 text-xs text-slate-500 font-medium" />
                                    <DataTable.Col label="PO" cellClassName="py-3 px-4">
                                        <ReferenceField source="po_id" reference="purchase_orders" link="show">
                                            <TextField source="reference" className="text-xs font-bold" />
                                        </ReferenceField>
                                    </DataTable.Col>
                                    <DataTable.Col source="reason" label="Reason" cellClassName="py-3 px-4 text-xs text-red-600 italic font-medium" />
                                </DataTable>
                            </ReferenceManyField>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export const VendorShow = () => {
    return (
        <ShowBase>
            <div className="h-full overflow-y-auto scrollbar-hide pb-10 pr-4">
                <VendorShowContent />
            </div>
        </ShowBase>
    );
};
